import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { CustomFieldType, ICustomFieldConfig } from 'src/services/custom-fields/custom-field.types';

import Label from 'src/components/label';

import { ColumnDef, ColumnFilterDef } from './data-list.types';
import { RowWithCustomFields, customFieldColumnKey, CustomFieldCellValue } from './custom-field-columns.types';

// ----------------------------------------------------------------------

type Translate = (key: string, options?: any) => string;

const dash = <Typography variant="body2" color="textSecondary">--</Typography>;

/** Chips past this are collapsed into a "+N", so a row with many values stays one line tall. */
const MAX_CHIPS = 2;

/**
 * Renders a list of values as chips, collapsing the overflow into "+N" with the full set on hover.
 * Shared with the labels columns so multi-value cells look the same wherever they appear.
 */
export function renderChipList(values: string[], max: number = MAX_CHIPS) {
  if (!values.length) return dash;

  const shown = values.slice(0, max);
  const extra = values.length - shown.length;

  return (
    <Tooltip title={values.join(', ')}>
      <Stack direction="row" alignItems="center" gap={0.5} flexWrap="nowrap">
        {shown.map((value) => (
          <Label key={value} variant="soft" color="default" sx={{ maxWidth: 140 }}>
            <Typography variant="caption" noWrap>{value}</Typography>
          </Label>
        ))}
        {extra > 0 && <Typography variant="caption" color="text.secondary">{`+${extra}`}</Typography>}
      </Stack>
    </Tooltip>
  );
}

/** The header filter a field's type deserves: pick-from-a-list beats type-a-substring-of-it. */
function filterForConfig(config: ICustomFieldConfig, t: Translate): ColumnFilterDef {
  switch (config.field_type) {
    case CustomFieldType.BOOLEAN:
      return {
        kind: 'boolean',
        trueLabel: t('common.yes', { defaultValue: 'Yes' }),
        falseLabel: t('common.no', { defaultValue: 'No' }),
        anyLabel: t('common.any', { defaultValue: 'Any' }),
      };

    case CustomFieldType.SELECT:
    case CustomFieldType.MULTI_SELECT:
      return {
        kind: 'select',
        multiple: true,
        // Archived options stay here on purpose. They are hidden from the record editor so nobody
        // picks them afresh, but records still hold them — and a value you can see in a column but
        // cannot filter to is worse than a slightly longer list.
        //
        // The value sent is the option id for MULTI_SELECT and the label for SELECT, which is what
        // each type actually stores; `select_options` already normalises both to {id, label}.
        options: (config.select_options ?? []).map((option) => ({
          value: option.id,
          label: option.label,
        })),
      };

    default:
      return { kind: 'text' };
  }
}

function renderValue(value: CustomFieldCellValue | undefined) {
  if (!value) return dash;

  switch (value.type) {
    case CustomFieldType.BOOLEAN:
      if (value.boolean === null || value.boolean === undefined) return dash;
      return <Typography variant="body2" color="text.secondary">{value.boolean ? 'Yes' : 'No'}</Typography>;

    case CustomFieldType.MULTI_SELECT:
      return renderChipList((value.options ?? []).map((option) => option.label).filter(Boolean));

    case CustomFieldType.SELECT:
      return value.text ? renderChipList([value.text]) : dash;

    // JT1-1827: a Phone field dials from the list like the record's own number
    case CustomFieldType.PHONE:
      return value.text
        ? (
          <Link href={`tel:${value.text}`} variant="body2" color="text.secondary" underline="hover" noWrap onClick={(event) => event.stopPropagation()}>
            {value.text}
          </Link>
        )
        : dash;

    default:
      return value.text
        ? <Typography variant="body2" color="text.secondary" noWrap>{value.text}</Typography>
        : dash;
  }
}

/**
 * Turns an org's custom field definitions into list columns, so a field an admin created shows up
 * in the column picker under the name they gave it.
 *
 * Keyed `cf:<config-id>` — the id rather than the label, so renaming a field doesn't orphan the
 * saved layouts that reference it. `resolveColumns` already tolerates a saved key the registry no
 * longer contains, so a deleted field's preference simply stops resolving.
 *
 * Hidden by default: five extra columns appearing unannounced on every list would be a worse
 * default than letting people turn on the ones they want.
 *
 * Not sortable in this version. An unmapped sort key falls back to created_at server-side, which
 * would look like the sort silently doing nothing; sorting on a value attached after the page query
 * needs a correlated subquery (see CompanyService.LEAN_COUNT_SORT_SQL) and is its own piece of work.
 */
export function buildCustomFieldColumns<TRow extends RowWithCustomFields>(
  configs: ICustomFieldConfig[] | undefined,
  t: Translate
): ColumnDef<TRow>[] {
  if (!configs?.length) return [];

  return configs.map((config) => ({
    key: customFieldColumnKey(config.id),
    label: config.label,
    defaultVisible: false,
    sortable: false,
    filter: filterForConfig(config, t),
    minWidth: 160,
    cardRole: 'meta' as const,
    render: (row: TRow) => renderValue(row.custom_fields?.[config.id]),
  }));
}
